import { motion, AnimatePresence } from "framer-motion";
import { Search, User, LogOut, LayoutDashboard, Network, RouterIcon, UserCog, Users, Bot, Globe, Building, ChevronDown, CheckCircle2, Settings, FileText, Activity, Logs, Workflow, Wrench, Calendar, Briefcase, Tag, ShieldPlus, Video, ChartLine, Database, FileStack } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { handleLogout } from "../auth/logout";
import { useClientLevel, setStoredLevel } from "../hooks/useClientLevel";
import baseapi from "../services/baseapi";
import { hasPermission } from "../utils/permissionsHelper";
import { ChatAssistant } from "../pages/ChatAssistant";



export const Dock = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [searchTerm, setSearchTerm] = useState("");
    const [profileOpen, setProfileOpen] = useState(false);
    const profileRef = useRef<HTMLDivElement>(null);
    const profileButtonRef = useRef<HTMLButtonElement>(null);
    const [profilePosition, setProfilePosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

    // Chat Assistant state
    const [isChatOpen, setIsChatOpen] = useState(false);

    const level = useClientLevel();
    const [clients, setClients] = useState<any[]>([]);
    const [isClientDropdownOpen, setIsClientDropdownOpen] = useState(false);
    const [clientSearchTerm, setClientSearchTerm] = useState("");
    const clientDropdownRef = useRef<HTMLDivElement>(null);

    const userInfo = localStorage.getItem('user');
    const userName = userInfo ? JSON.parse(userInfo).name : "";
    const role = userInfo ? JSON.parse(userInfo).role : "";

    const syncLevelWithClients = useCallback((freshClients: any[]) => {
        if (!level || level.name === "global" || !level.id) return;
        const matched = freshClients.find((c: any) => c.id === level.id);
        if (!matched) return;

        const hasChanged =
            matched.name !== level.name ||
            matched.display_name !== level.display_name || matched.install_first_agent !== level.install_first_agent;

        if (hasChanged) {
            setStoredLevel({
                ...level,
                name: matched.name,
                display_name: matched.display_name,
                install_first_agent: matched.install_first_agent,
            });
        }
    }, [level]);

    const fetchClientData = async () => {
        try {
            const params = {
                query: JSON.stringify({}),
                skip: 0,
                limit: 100,
                fields: ["name", "display_name", "id"],
            };
            const response = await baseapi.get("/clients", { params });
            if (response.data.status) {
                const fresh = response.data.data;
                setClients(fresh);
                syncLevelWithClients(fresh);
            }
        } catch (error: any) {
            console.error("Failed to fetch clients:", error.message);
        }
    };

    useEffect(() => {
        fetchClientData();
    }, []);

    useEffect(() => {
        if (clients.length > 0) {
            syncLevelWithClients(clients);
        }
    }, [clients, syncLevelWithClients]);

    useEffect(() => {
        const handleClientsUpdate = () => fetchClientData();
        window.addEventListener('clients-updated', handleClientsUpdate);
        return () => window.removeEventListener('clients-updated', handleClientsUpdate);
    }, [fetchClientData]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (clientDropdownRef.current && !clientDropdownRef.current.contains(event.target as Node)) {
                setIsClientDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleClientSelect = async (newLevel: any) => {
        // if (newLevel.name !== "global" && newLevel.id) {
        //     try {
        //         const response = await baseapi.get("/installation_secret/get-secret-key", {
        //             params: { client_id: newLevel.id },
        //         });
        //         if (response.data.secret_key) newLevel.secret_key = response.data.secret_key;
        //     } catch (error: any) {
        //         console.error(error.message);
        //     }
        // }

        if (level.name === "global" && location.pathname === "/clients" && newLevel.name !== "global") {
            navigate("/dashboard");
        }

        if (location.pathname.startsWith("/custom-dashboard")) {
            if (newLevel.name === "global") {
                navigate("/dashboard");
            } else {
                navigate("/custom-dashboard-v2");
            }
        }

        if (location.pathname.startsWith("/customreports")) {
            if (newLevel.name === "global") {
                navigate("/dashboard");
            } else if (location.pathname.includes("/builder")) {
                navigate("/customreports");
            }
        }

        setStoredLevel(newLevel);
        setIsClientDropdownOpen(false);
        setClientSearchTerm("");
    };

    const filteredClients = clients.filter((client: any) =>
        client.name?.toLowerCase().includes(clientSearchTerm.toLowerCase())
    );

    // const capitalize = (text?: string) =>
    //     text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : "";

    const BASE_DOCK_ITEMS = [
        { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, path: "/dashboard", color: "bg-purple-500/30" },
        // { id: "custom-dashboard", label: "Custom Dashboard", icon: ChartLine, path: "/custom-dashboard", clientOnly: true, color: "bg-cyan-500/30" },
        { id: "custom-dashboard-v2", label: "Custom Dashboard V2", icon: ChartLine, path: "/custom-dashboard-v2", clientOnly: true, permissionKey: "cdash", color: "bg-cyan-500/30" },
        { id: "external-scan", label: "External Scan", icon: Globe, path: "/external-scan", clientOnly: true, color: "bg-amber-500/30" },
        { id: "assets", label: "Assets", icon: RouterIcon, path: "/asset", permissionKey: "assets", color: "bg-sky-600/30" },
        { id: "agents", label: "Agents", icon: Bot, path: "/agents", permissionKey: "agents", color: "bg-gray-600/30" },
        { id: "kubernetes", label: "Kubernetes", icon: "/kubernetes.svg", path: "/kubernetes", permissionKey: "agents", color: "bg-teal-600/30" },
        { id: "clients", label: "Clients", icon: Users, path: "/clients", permissionKey: "clients", globalOnly: true, color: "bg-cyan-700/50" },
        { id: "topology", label: "Topology", icon: Network, path: "/topology", permissionKey: "topology", clientOnly: true, color: "bg-indigo-600/30" },
        // { id: "db-topology", label: "DB Topology", icon: Database, path: "/db-topology", permissionKey: "topology", clientOnly: true, color: "bg-orange-600/30" },
        { id: "db-monitor", label: "DB Monitor", icon: Database, path: "/db-monitor", permissionKey: "topology", clientOnly: true, color: "bg-emerald-600/30" },
        { id: "cameras", label: "Monitoring System", icon: Video, path: "/cameras", clientOnly: true, permissionKey: "camera", color: "bg-pink-600/30" },
        { id: "reports", label: "Reports", icon: FileText, path: "/reports", permissionKey: "reports", color: "bg-blue-600/30" },
        { id: "custom-reports", label: "Custom Reports", icon: FileStack, path: "/customreports", clientOnly: true, color: "bg-fuchsia-600/30" },
        { id: "automation", label: "Automation", icon: UserCog, path: "/automation", color: "bg-orange-600/30", permissionkey: "rule_engine_config" },
        { id: "events", label: "Events", icon: Activity, path: "/events", permissionKey: "events", color: "bg-red-600/30" },
        { id: "audit-logs", label: "Audit Logs", icon: Logs, path: "/audit-logs", permissionKey: "audit_logs", color: "bg-amber-600/30" },
        { id: "patching", label: "Patching", icon: ShieldPlus, path: "/patching", permissionKey: "agent_patch_jobs", clientOnly: true, color: "bg-red-500/30" },
        { id: "digital-twin", label: "Digital Twin", icon: Globe, path: "/digital-twin", globalOnly: true, color: "bg-blue-600/30" },
        { id: "jobs", label: "Jobs", icon: Briefcase, path: "/jobs", permissionKey: "jobs", color: "bg-blue-700/30", hiddenFromDock: false },
        { id: "settings", label: "Settings", icon: Settings, path: "/settings?tab=api_integration", color: "bg-purple-600/30" },
        { id: "integrations", label: "Integrations", icon: Workflow, path: "/settings?tab=api_integration", permissionKey: "integrations", color: "bg-teal-600/30", hiddenFromDock: true },
        { id: "configuration", label: "Configuration", icon: Wrench, path: "/settings?tab=configuration", permissionKey: "ticketing", color: "bg-slate-600/30", hiddenFromDock: true },
        { id: "scheduler", label: "Scheduler", icon: Calendar, path: "/settings?tab=scheduler", permissionKey: "scheduler", color: "bg-cyan-600/30", hiddenFromDock: true },
        { id: "tags", label: "Tags", icon: Tag, path: "/settings?tab=tags", permissionKey: "tags", color: "bg-yellow-600/30", hiddenFromDock: true },
        { id: "users", label: "Users", icon: User, path: "/settings?tab=users", permissionKey: "users", color: "bg-orange-600/30", hiddenFromDock: true },
        { id: "roles", label: "Roles", icon: UserCog, path: "/settings?tab=roles", permissionKey: "roles", color: "bg-green-600/30", hiddenFromDock: true },
    ];

    const DOCK_ITEMS = useMemo(() => {
        return BASE_DOCK_ITEMS.filter(item => {
            if (item.globalOnly && level?.name !== "global") return false;
            if (item.clientOnly && level?.name === "global") return false;
            if (item.permissionKey) return hasPermission(item.permissionKey, 'read');
            return true;
        });
    }, [level]);

    const isActive = (path: string) => {
        const basePath = path.split('?')[0];
        return location.pathname.startsWith(basePath);
    };

    const filteredItems = searchTerm.trim() === ""
        ? DOCK_ITEMS.filter(item => !item.hiddenFromDock)
        : DOCK_ITEMS.filter(item =>
            item.label.toLowerCase().includes(searchTerm.toLowerCase())
        );

    useEffect(() => {
        if (!profileOpen) return;
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                profileRef.current && !profileRef.current.contains(target) &&
                profileButtonRef.current && !profileButtonRef.current.contains(target)
            ) {
                setProfileOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [profileOpen]);

    const computeProfilePosition = () => {
        if (!profileButtonRef.current) return;
        const rect = profileButtonRef.current.getBoundingClientRect();
        const panelWidth = 192;
        const panelMargin = 8;
        const estimatedHeight = 150;

        let left = rect.left;
        let top = rect.top - estimatedHeight - panelMargin;

        if (top < panelMargin) top = panelMargin;

        const rightOverflow = left + panelWidth - window.innerWidth;
        if (rightOverflow > 0) left = Math.max(panelMargin, left - rightOverflow - panelMargin);

        setProfilePosition({ top: Math.round(top), left: Math.round(left) });
    };

    const toggleProfile = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!profileOpen) computeProfilePosition();
        setProfileOpen(prev => !prev);
    };

    useEffect(() => {
        if (!profileOpen) return;
        const update = () => computeProfilePosition();
        window.addEventListener("resize", update);
        window.addEventListener("scroll", update, true);
        return () => {
            window.removeEventListener("resize", update);
            window.removeEventListener("scroll", update, true);
        };
    }, [profileOpen]);

    const handleLogoutClick = () => {
        handleLogout();
        setProfileOpen(false);
    };

    // Calculate dynamic styles based on client name length to prevent mascot cutoff
    const clientName = level?.name === "global" ? "Global" : (level?.name || "");
    const clientNameLength = clientName.length;
    const isLongName = clientNameLength > 12;
    const isVeryLongName = clientNameLength > 20;

    const buttonPadding = isVeryLongName
        ? "px-2 py-1 min-h-[28px]"
        : isLongName
            ? "px-2.5 py-1.5 min-h-[32px]"
            : "px-3 py-2 min-h-[36px]";
    const textClass = isVeryLongName
        ? "text-[10px]"
        : isLongName
            ? "text-[11px]"
            : "text-xs";
    const textMaxWidth = isVeryLongName
        ? "max-w-[100px]"
        : isLongName
            ? "max-w-[130px]"
            : "max-w-[180px]";

    return (
        <>
            <ChatAssistant isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} />

            {/* ── Main Container ──────────────────────────────────────────────── */}
            <div className="hidden lg:block fixed bottom-4 left-0 right-0 mx-auto z-40 px-2 pointer-events-none">
                <div className="relative flex items-end justify-center max-w-fit mx-auto pointer-events-auto">

                    {/* The Glass Dock */}
                    <div className="backdrop-blur-md bg-gray-900/70 border border-gray-700 rounded-2xl px-3 py-2 flex items-center gap-3 shadow-black/40 shadow-lg">

                        {/* Nav items */}
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                            {filteredItems.length > 0 ? (
                                filteredItems.map(item => (
                                    <DockIcon
                                        key={item.id}
                                        item={item}
                                        active={isActive(item.path)}
                                        onClick={() => { navigate(item.path); setSearchTerm(""); }}
                                    />
                                ))
                            ) : (
                                <div className="text-sm text-gray-400 px-2">No results.</div>
                            )}
                        </div>

                        <div className="w-px h-7 bg-gray-700 flex-shrink-0" />

                        {/* Search */}
                        <div className="relative flex-shrink-0">
                            <Search className="w-3 h-3 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                placeholder="Search apps..."
                                className="bg-gray-800/70 border border-gray-700 rounded-lg pl-6 pr-3 py-1.5 text-xs text-white placeholder-gray-400 focus:outline-none focus:border-orange-500 w-40"
                            />
                        </div>

                        <div className="w-px h-7 bg-gray-700 flex-shrink-0" />

                        {/* Profile button */}
                        <div className="relative group flex-shrink-0">
                            <button
                                ref={profileButtonRef}
                                onClick={toggleProfile}
                                className={`${profileOpen ? "bg-orange-600" : "bg-orange-500"} hover:bg-orange-600 hover:scale-110 text-white p-2.5 rounded-xl transition-all duration-200 hover:shadow-lg flex-shrink-0`}
                            >
                                <User className="w-4 h-4" />
                            </button>
                            {!profileOpen && (
                                <div className="absolute -top-12 left-1/2 -translate-x-1/2 
                px-2 py-1 rounded bg-gray-800/95 border border-gray-700 
                text-white text-xs opacity-0 group-hover:opacity-100 
                transition-opacity duration-200 pointer-events-none 
                whitespace-nowrap z-50">

                                    Profile

                                    {/* Arrow */}
                                    <div className="absolute left-1/2 -bottom-1.5 -translate-x-1/2 
                  w-2.5 h-2.5 bg-gray-800 border-r border-b border-gray-700 
                  rotate-45"></div>
                                </div>
                            )}
                        </div>

                        {/* Client selector */}
                        {level?.name && (
                            <>
                                <div className="w-px h-6 bg-gray-600 flex-shrink-0" />
                                <div className="relative flex-shrink-0" ref={clientDropdownRef}>
                                    <button
                                        onClick={() => setIsClientDropdownOpen(prev => !prev)}
                                        className={`bg-gradient-to-r from-orange-500/20 to-amber-600/20 border border-orange-500/30 rounded-lg flex items-center gap-1.5 hover:from-orange-500/30 hover:to-amber-600/30 transition-all duration-200 ${buttonPadding}`}
                                    >
                                        <div className="w-1.5 h-1.5 bg-orange-400 rounded-full animate-pulse flex-shrink-0" />
                                        <span className={`text-orange-300 font-medium truncate ${textClass} ${textMaxWidth}`}>
                                            {clientName}
                                        </span>
                                        <ChevronDown className={`w-3 h-3 text-orange-400/70 transition-transform duration-200 flex-shrink-0 ${isClientDropdownOpen ? "rotate-180" : ""}`} />
                                    </button>

                                    <AnimatePresence>
                                        {isClientDropdownOpen && (
                                            <motion.div
                                                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                                animate={{ opacity: 1, y: -8, scale: 1 }}
                                                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                                className="absolute bottom-full right-0 mb-2 w-64 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl z-[9999] overflow-hidden"
                                            >
                                                {/* Search */}
                                                <div className="p-2 bg-gray-950/50 border-b border-gray-700">
                                                    <div className="relative">
                                                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
                                                        <input
                                                            type="text"
                                                            placeholder="Filter clients..."
                                                            value={clientSearchTerm}
                                                            onChange={e => setClientSearchTerm(e.target.value)}
                                                            className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-gray-200 focus:outline-none focus:border-orange-500/50"
                                                        />
                                                    </div>
                                                </div>

                                                <div className="max-h-60 overflow-y-auto custom-scroll py-1">
                                                    {/* Global option */}
                                                    <button
                                                        onClick={() => handleClientSelect({ name: "global" })}
                                                        className="w-full px-3 py-2 flex items-center gap-2.5 hover:bg-gray-800 transition-colors"
                                                    >
                                                        <Globe className="w-3.5 h-3.5 text-indigo-400" />
                                                        <div className="text-left flex-1 min-w-0">
                                                            <p className="text-[11px] font-bold text-gray-200">Global</p>
                                                        </div>
                                                        {level.name === "global" && (
                                                            <CheckCircle2 className="w-3 h-3 text-orange-500" />
                                                        )}
                                                    </button>

                                                    {/* Client list */}
                                                    {filteredClients.map(client => (
                                                        <button
                                                            key={client.id}
                                                            onClick={() => handleClientSelect({
                                                                id: client.id,
                                                                name: client.name,
                                                                display_name: client.display_name,
                                                                client_id: client.client_id,
                                                                install_first_agent: client.install_first_agent,
                                                            })}
                                                            className="w-full px-3 py-2 flex items-center gap-2.5 hover:bg-gray-800 transition-colors"
                                                        >
                                                            <Building className="w-3.5 h-3.5 text-blue-400" />
                                                            <div className="text-left flex-1 min-w-0">
                                                                <p className="text-[11px] font-bold text-gray-200 truncate">
                                                                    {client.name}
                                                                </p>
                                                                <p className="text-[9px] text-gray-500 truncate">
                                                                    {client.display_name || "Enterprise Client"}
                                                                </p>
                                                            </div>
                                                            {level.id === client.id && (
                                                                <CheckCircle2 className="w-3 h-3 text-orange-500" />
                                                            )}
                                                        </button>
                                                    ))}
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            </>
                        )}
                    </div>

                    {/* ── Owl Mascot & Chat Bubble (Positioned absolutely so it doesn't shift the dock) ── */}
                    <div className="absolute left-full bottom-0 mb-1 flex-shrink-0 pointer-events-auto">
                        <div className="flex flex-row items-end pb-1 whitespace-nowrap">
                            <button onClick={() => setIsChatOpen(!isChatOpen)}>
                                <img
                                    src="/owl.gif"
                                    alt="Owl mascot"
                                    className="w-24 h-16 object-contain hover:scale-110 transition-transform duration-200"
                                />
                            </button>
                            {!isChatOpen ? (
                                <div className="w-60 h-16">
                                    <img src="/bubble.svg" alt="Chat Bubble" />
                                </div>
                            ) : (
                                <div className="w-40 h-16">
                                    <img src="/bubble2.svg" alt="Chat Bubble" />
                                </div>
                            )}
                        </div>
                    </div>

                </div>
            </div>

            {/* ── Floating Profile Panel ────────────────────────────────────── */}
            {profileOpen && (
                <div
                    ref={profileRef}
                    style={{ position: "fixed", top: profilePosition.top, left: profilePosition.left }}
                    className="bg-gray-900/95 backdrop-blur-md border border-gray-700 rounded-xl p-3 w-48 shadow-2xl z-50"
                >
                    <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-3 p-2 border-b border-gray-700 pb-3">
                            <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                                <User className="w-4 h-4 text-white" />
                            </div>
                            <div>
                                <p className="text-white text-sm font-medium">{userName}</p>
                                <p className="text-gray-400 text-xs">{role}</p>
                            </div>
                        </div>
                        <button
                            onClick={() => { navigate('/sop'); setProfileOpen(false); }}
                            className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors duration-200 text-gray-300 hover:text-white"
                        >
                            <FileText className="w-4 h-4" />
                            <span className="text-sm font-medium">Documentation</span>
                        </button>
                        <button
                            onClick={handleLogoutClick}
                            className="flex items-center gap-3 p-2 rounded-lg hover:bg-red-500/20 transition-colors duration-200 text-red-400 hover:text-red-300"
                        >
                            <LogOut className="w-4 h-4" />
                            <span className="text-sm font-medium">Logout</span>
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

// ── DockIcon ──────────────────────────────────────────────────────────────────
interface DockItem {
    id: string;
    label: string;
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>> | string;
    path: string;
    color?: string;
    hiddenFromDock?: boolean;
}

const DockIcon = ({
    item,
    active,
    onClick,
}: {
    item: DockItem;
    active: boolean;
    onClick: () => void;
}) => (
    <div className="relative group flex-shrink-0">
        <div className="absolute -top-11 left-1/2 -translate-x-1/2 
                px-2 py-1 rounded bg-gray-800/95 border border-gray-700 
                text-white text-xs opacity-0 group-hover:opacity-100 
                transition-opacity duration-200 pointer-events-none 
                whitespace-nowrap z-50">

            {item.label}

            {/* Arrow */}
            <div className="absolute left-1/2 -bottom-1.5 -translate-x-1/2 
                  w-2.5 h-2.5 bg-gray-800 border-r border-b border-gray-700 
                  rotate-45"></div>
        </div>
        <motion.button
            onClick={onClick}
            initial={{ scale: 1 }}
            whileHover={{ scale: 1.15, translateY: -4 }}
            whileTap={{ scale: 0.95 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
            className={`w-10 h-10 flex items-center justify-center rounded-xl border transition-colors duration-200 flex-shrink-0
                ${item.color || "bg-gray-800/70"}
                ${active
                    ? "border-orange-400 text-white shadow-lg"
                    : "border-transparent text-white hover:brightness-125"
                }`}
        >
            {typeof item.icon === "string" ? (
                <img src={item.icon} className="w-6 h-6 object-contain" alt={item.label} />
            ) : (
                <item.icon className="w-4 h-4" />
            )}
        </motion.button>
    </div>
);